import React, { useRef, useCallback, useMemo } from "react";
import { ViewersList } from "./ViewersList";
import { formatDate } from "../utils/date";
import { MentionEditor } from "./Editor";
import { ThumbsUp, MessageCircle, Eye, MoreVertical } from "lucide-react";
import { FilePreview } from "./FilePreview";
import { InView } from "react-intersection-observer";

const CommentItem = React.memo(function CommentItem({
  comment,
  currentUser,
  onReply,
  onEdit,
  onLike,
  isLiked,
  isReplying,
  backendUrl,
  profilePicture,
  handleSeen,
  handleRemoveFile,
  onDeleteComment,
}) {
  const [showViewers, setShowViewers] = React.useState(false);
  const [, setShowLikes] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);

  const menuRef = useRef(null);
  let commentRef;

  const isCurrentUserComment = useMemo(
    () => +currentUser === comment?.user?.id,
    [currentUser, comment?.user?.id]
  );

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleEdit = useCallback(
    (content, files) => {
      onEdit(comment.id, content, files);
      setIsEditing(false);
    },
    [onEdit, comment.id]
  );

  const handleDeleteCommentAction = useCallback(
    (commentID) => {
      onDeleteComment(commentID);
    },
    [onDeleteComment]
  );

  return (
    <InView
      onChange={(inView) => {
        if (inView) {
          handleSeen(comment, "comment");
        }
      }}
      rootMargin={"100px"}
      threshold={1}
      key={comment.id}
    >
      <div ref={commentRef} className="relative">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
            <img
              src={
                comment?.user?.avatar?.url
                  ? `${backendUrl}${comment?.user?.avatar?.url}`
                  : profilePicture
              }
              alt={comment?.user?.username}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1">
            <div className="speech-bubble-reply relative">
              <div className="absolute -left-[10px] top-3 w-0 h-0 border-t-[6px] border-t-transparent border-r-[10px] border-r-blue-50 border-b-[6px] border-b-transparent" />
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex flex-col">
                    <h4 className="font-semibold leading-4 text-lg">
                      {comment?.user?.username}
                    </h4>
                    <span className="text-sm text-gray-600">
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                  {isCurrentUserComment && (
                    <div className="relative" ref={menuRef}>
                      <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-blue-100/50 transition-colors"
                      >
                        <MoreVertical size={14} />
                      </button>
                      {showMenu && (
                        <div className="absolute right-0 mt-1 py-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                          <button
                            onClick={() => {
                              setIsEditing(true);
                              setShowMenu(false);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                            <span>Editar comentario</span>
                          </button>
                          <button
                            onClick={() => {
                              handleDeleteCommentAction(comment.id);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            <span>Eliminar comentario</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div className="mt-2">
                    <MentionEditor
                      onPublish={handleEdit}
                      initialContent={comment.content}
                      buttonText="Save"
                      backendUrl={backendUrl}
                      existingFiles={comment.media}
                      onRemoveExistingFile={handleRemoveFile}
                    />
                  </div>
                ) : (
                  <>
                    <div
                      className="text-gray-800 prose prose-md w-fit	break-words"
                      dangerouslySetInnerHTML={{ __html: comment.content }}
                    />
                    {comment.media && comment.media.length > 0 && (
                      <FilePreview
                        files={comment.media}
                        backendUrl={backendUrl}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="mt-2 flex items-center gap-4">
              <div className="relative">
                <button
                  onClick={() => onLike(null, comment.id)}
                  onMouseEnter={() => setShowLikes(true)}
                  onMouseLeave={() => setShowLikes(false)}
                  className={`flex items-center gap-1.5 ${
                    isLiked
                      ? "text-blue-500 bg-blue-50"
                      : "text-gray-400 hover:bg-gray-100"
                  } px-2.5 py-1.5 rounded-full transition-all duration-200`}
                >
                  <ThumbsUp
                    size={14}
                    className={isLiked ? "fill-current" : ""}
                    strokeWidth={2}
                  />
                  <span className="text-sm font-semibold">
                    {comment.likes.length}
                  </span>
                </button>
                {/* <LikesList
                  show={showLikes}
                  users={comment.likes}
                  backendUrl={backendUrl}
                  profilePicture={profilePicture}
                  avatar={avatar}
                /> */}
              </div>

              <button
                onClick={() => onReply(comment)}
                className={`flex items-center gap-1.5 ${
                  isReplying
                    ? "text-blue-500 bg-blue-50"
                    : "text-gray-400 hover:bg-gray-100"
                } px-2.5 py-1.5 rounded-full transition-all duration-200`}
              >
                <MessageCircle size={14} strokeWidth={2} />
                <span className="text-sm font-semibold">Responder</span>
              </button>
              <div className="relative">
                <button
                  className="flex items-center gap-1.5 text-gray-400 hover:bg-gray-100 px-2.5 py-1.5 rounded-full transition-all duration-200"
                  onMouseEnter={() => setShowViewers(true)}
                  onMouseLeave={() => setShowViewers(false)}
                >
                  <Eye size={14} strokeWidth={2} />
                  <span className="text-sm font-semibold">
                    {comment.seen_by.length}
                  </span>
                </button>
                <ViewersList
                  show={showViewers}
                  viewers={comment.seen_by}
                  backendUrl={backendUrl}
                  profilePicture={profilePicture}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </InView>
  );
});

export { CommentItem };
